/**
 * Curated Canva-like text style presets for the "Текст" panel (Phase 3E,
 * task 4). Each preset is a fixed bundle of style fields — font, weight,
 * style, size, colors — that gets applied either to the currently selected
 * text/sticker layer or used to spin up a brand-new layer (see
 * `TextPanel.tsx` and `editorStore.addPresetLayer`).
 *
 * `fontFamily` values MUST be valid `fonts.ts` keys — enforced by
 * `__tests__/textPresets.test.ts`.
 */
import type { EditorOverlayLayer } from '@/types/editor';

export interface TextPreset {
  id: string;
  /** Display name shown under the live preview. */
  name: string;
  defaultText?: string;
  description?: string;
  category?: 'Заголовки' | 'Подписи' | 'Акценты';
  previewBackground?: string;
  layerType: 'text' | 'sticker';
  fontFamily: string;
  fontWeight: EditorOverlayLayer['fontWeight'];
  fontStyle: EditorOverlayLayer['fontStyle'];
  size: number;
  color: string;
  /** Badge/sticker background; text presets omit this (transparent). */
  backgroundColor?: string;
  badgeStyle?: EditorOverlayLayer['badgeStyle'];
  badgeAccentColor?: string;
  badgeBorderColor?: string;
  badgeBorderWidth?: number;
  underline?: boolean;
}

export const TEXT_PRESETS: TextPreset[] = [
  {
    id: 'reels-hook',
    name: 'Хук для Reels',
    defaultText: 'СМОТРИ ДО КОНЦА',
    description: 'Крупный контрастный хук',
    category: 'Заголовки',
    previewBackground: 'linear-gradient(135deg,#312e81,#111827)',
    layerType: 'text',
    fontFamily: 'bebas',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 154,
    color: '#ffffff',
  },
  {
    id: 'editorial-title',
    name: 'Редакционный',
    defaultText: 'Новая история',
    description: 'Элегантный serif-заголовок',
    category: 'Заголовки',
    previewBackground: 'linear-gradient(135deg,#451a03,#1c1917)',
    layerType: 'text',
    fontFamily: 'playfair',
    fontWeight: '700',
    fontStyle: 'italic',
    size: 126,
    color: '#fef3c7',
  },
  {
    id: 'sports-score',
    name: 'Спортивный',
    defaultText: 'MATCH DAY',
    description: 'Плотный спортивный титр',
    category: 'Заголовки',
    previewBackground: 'linear-gradient(135deg,#7f1d1d,#111827)',
    layerType: 'text',
    fontFamily: 'oswald',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 142,
    color: '#ffffff',
  },
  {
    id: 'clean-title',
    name: 'Минималистичный',
    defaultText: 'Простые решения',
    description: 'Чистая современная подача',
    category: 'Заголовки',
    previewBackground: 'linear-gradient(135deg,#0f172a,#020617)',
    layerType: 'text',
    fontFamily: 'manrope',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 112,
    color: '#ffffff',
  },
  {
    id: 'tech-neon',
    name: 'Tech Neon',
    defaultText: 'NEXT LEVEL',
    description: 'Яркий технологичный акцент',
    category: 'Заголовки',
    previewBackground: 'linear-gradient(135deg,#164e63,#111827)',
    layerType: 'text',
    fontFamily: 'space-grotesk',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 126,
    color: '#67e8f9',
  },
  {
    id: 'subtitle-clean',
    name: 'Чистая подпись',
    defaultText: 'Короткое пояснение к кадру',
    description: 'Универсальная подпись',
    category: 'Подписи',
    previewBackground: 'linear-gradient(135deg,#1f2937,#111827)',
    layerType: 'text',
    fontFamily: 'dm-sans',
    fontWeight: '600',
    fontStyle: 'normal',
    size: 54,
    color: '#e5e7eb',
  },
  {
    id: 'documentary-caption',
    name: 'Документальный',
    defaultText: 'Алматы · Июль 2026',
    description: 'Место и время съёмки',
    category: 'Подписи',
    previewBackground: 'linear-gradient(135deg,#292524,#0c0a09)',
    layerType: 'text',
    fontFamily: 'inter',
    fontWeight: '600',
    fontStyle: 'normal',
    size: 48,
    color: '#f5f5f4',
  },
  {
    id: 'quote-modern',
    name: 'Современная цитата',
    defaultText: '«Идеи меняют всё»',
    description: 'Цитата с характером',
    category: 'Подписи',
    previewBackground: 'linear-gradient(135deg,#4c1d95,#111827)',
    layerType: 'text',
    fontFamily: 'playfair',
    fontWeight: '700',
    fontStyle: 'italic',
    size: 102,
    color: '#ddd6fe',
  },
  {
    id: 'creator-name',
    name: 'Имя автора',
    defaultText: '@CREATOR',
    description: 'Ник или подпись автора',
    category: 'Подписи',
    previewBackground: 'linear-gradient(135deg,#134e4a,#111827)',
    layerType: 'text',
    fontFamily: 'montserrat',
    fontWeight: '700',
    fontStyle: 'normal',
    size: 66,
    color: '#99f6e4',
  },
  {
    id: 'accent-marker',
    name: 'Маркер',
    defaultText: 'ВАЖНО',
    description: 'Яркий смысловой акцент',
    category: 'Акценты',
    previewBackground: 'linear-gradient(135deg,#422006,#111827)',
    layerType: 'sticker',
    fontFamily: 'space-grotesk',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 66,
    color: '#422006',
    backgroundColor: '#fde047',
    badgeAccentColor: '#fb923c',
    badgeStyle: 'pill',
  },
  {
    id: 'chapter-label',
    name: 'Глава',
    defaultText: 'ГЛАВА 01',
    description: 'Навигация по сюжету',
    category: 'Акценты',
    previewBackground: 'linear-gradient(135deg,#172554,#111827)',
    layerType: 'sticker',
    fontFamily: 'space-grotesk',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 56,
    color: '#dbeafe',
    backgroundColor: '#1e40af',
    badgeAccentColor: '#4f46e5',
    badgeStyle: 'soft-square',
  },
  {
    id: 'price-highlight',
    name: 'Цена',
    defaultText: '4 990 ₸',
    description: 'Коммерческий ценник',
    category: 'Акценты',
    previewBackground: 'linear-gradient(135deg,#14532d,#111827)',
    layerType: 'sticker',
    fontFamily: 'montserrat',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 82,
    color: '#052e16',
    backgroundColor: '#86efac',
    badgeAccentColor: '#2dd4bf',
    badgeStyle: 'rounded',
  },
  {
    id: 'urgent-label',
    name: 'Срочно',
    defaultText: 'СРОЧНО',
    description: 'Новостной акцент',
    category: 'Акценты',
    previewBackground: 'linear-gradient(135deg,#7f1d1d,#111827)',
    layerType: 'sticker',
    fontFamily: 'oswald',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 72,
    color: '#ffffff',
    backgroundColor: '#dc2626',
    badgeAccentColor: '#f97316',
    badgeStyle: 'soft-square',
  },
  {
    id: 'luxury-label',
    name: 'Премиум',
    defaultText: 'EXCLUSIVE',
    description: 'Сдержанный luxury-акцент',
    category: 'Акценты',
    previewBackground: 'linear-gradient(135deg,#292524,#0c0a09)',
    layerType: 'sticker',
    fontFamily: 'playfair',
    fontWeight: '700',
    fontStyle: 'italic',
    size: 64,
    color: '#fef3c7',
    backgroundColor: '#1c1917',
    badgeAccentColor: '#44403c',
    badgeBorderColor: '#d6b86a',
    badgeBorderWidth: 4,
    badgeStyle: 'outline',
  },
];

/** Editable commercial badges. Unlike PNG sticker packs, their copy remains editable on canvas. */
export const BADGE_PRESETS: TextPreset[] = [
  {
    id: 'badge-sale-gradient',
    name: 'SALE',
    layerType: 'sticker',
    fontFamily: 'bebas',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 96,
    color: '#ffffff',
    backgroundColor: '#ff2e63',
    badgeAccentColor: '#ff7a18',
    badgeBorderColor: '#ffd0dc',
    badgeBorderWidth: 5,
    badgeStyle: 'rounded',
  },
  {
    id: 'badge-new-pill',
    name: 'НОВИНКА',
    layerType: 'sticker',
    fontFamily: 'space-grotesk',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 70,
    color: '#082f49',
    backgroundColor: '#67e8f9',
    badgeAccentColor: '#38bdf8',
    badgeBorderColor: '#e0f2fe',
    badgeBorderWidth: 4,
    badgeStyle: 'pill',
  },
  {
    id: 'badge-discount-pop',
    name: '−20%',
    layerType: 'sticker',
    fontFamily: 'montserrat',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 88,
    color: '#ffffff',
    backgroundColor: '#5b35ff',
    badgeAccentColor: '#db2777',
    badgeBorderColor: '#c4b5fd',
    badgeBorderWidth: 5,
    badgeStyle: 'soft-square',
  },
  {
    id: 'badge-buy-cta',
    name: 'КУПИТЬ',
    layerType: 'sticker',
    fontFamily: 'dm-sans',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 72,
    color: '#ffffff',
    backgroundColor: '#15803d',
    badgeAccentColor: '#22c55e',
    badgeBorderColor: '#bbf7d0',
    badgeBorderWidth: 4,
    badgeStyle: 'pill',
  },
  {
    id: 'badge-hot-flame',
    name: 'HOT',
    layerType: 'sticker',
    fontFamily: 'oswald',
    fontWeight: '800',
    fontStyle: 'italic',
    size: 94,
    color: '#ffffff',
    backgroundColor: '#ea580c',
    badgeAccentColor: '#ef4444',
    badgeBorderColor: '#fed7aa',
    badgeBorderWidth: 5,
    badgeStyle: 'rounded',
  },
  {
    id: 'badge-limited-premium',
    name: 'LIMITED',
    layerType: 'sticker',
    fontFamily: 'bebas',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 76,
    color: '#fef3c7',
    backgroundColor: '#111827',
    badgeAccentColor: '#1f2937',
    badgeBorderColor: '#f59e0b',
    badgeBorderWidth: 5,
    badgeStyle: 'outline',
  },
  {
    id: 'badge-one-plus-one',
    name: '1+1',
    layerType: 'sticker',
    fontFamily: 'montserrat',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 94,
    color: '#ffffff',
    backgroundColor: '#7c3aed',
    badgeAccentColor: '#ec4899',
    badgeBorderColor: '#ddd6fe',
    badgeBorderWidth: 5,
    badgeStyle: 'pill',
  },
  {
    id: 'badge-gift',
    name: 'ПОДАРОК',
    layerType: 'sticker',
    fontFamily: 'space-grotesk',
    fontWeight: '800',
    fontStyle: 'normal',
    size: 68,
    color: '#ffffff',
    backgroundColor: '#db2777',
    badgeAccentColor: '#f43f5e',
    badgeBorderColor: '#fbcfe8',
    badgeBorderWidth: 4,
    badgeStyle: 'rounded',
  },
];

/**
 * Style-only patch derived from a preset — deliberately excludes
 * `text`/`x`/`y`/`start`/`end` so applying it to an already-positioned,
 * already-timed layer keeps its content and placement untouched.
 */
export function presetToLayerPatch(preset: TextPreset): Partial<EditorOverlayLayer> {
  const patch: Partial<EditorOverlayLayer> = {
    fontFamily: preset.fontFamily,
    fontWeight: preset.fontWeight,
    fontStyle: preset.fontStyle,
    size: preset.size,
    color: preset.color,
    textColor: preset.color,
    backgroundColor: preset.backgroundColor ?? 'transparent',
    badgeStyle: preset.badgeStyle,
    badgeAccentColor: preset.badgeAccentColor,
    badgeBorderColor: preset.badgeBorderColor,
    badgeBorderWidth: preset.badgeBorderWidth,
    underline: preset.underline ?? false,
  };
  return patch;
}
